//codex-- Page-scoped toolbar layout; retain this project's handlers and data.
import PageToolbar, { ToolbarField, PageToolbarSearch as SearchBarCP } from './PageToolbar';
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { NavLink, useNavigate } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import HeadingCp from '../components/HeadingCp';
import SubMenu from '../components/SubMenu';
import DateFormat from '../components/DateFormat';
import Modal from '../components/Modal';
import OperationFormModal from '../components/OperationFormModal';
import { CheckBadgeIcon, DocumentCheckIcon } from '@heroicons/react/24/outline';
import ModalWindow from '../components/ModalWindow';

const OperationScheduleListView = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const api = `${apiUrl}/operation_crud.php`;
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [ops, setOps] = useState([]);
  const [technicians, setTechnicians] = useState([]);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [notesIdOpen, setNotesIdOpen] = useState(null);  
  const [userNotesIdOpen, setUserNotesIdOpen] = useState(null);

  // modal/form state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    id: '',
    description: '',
    status: '',
    date: '',
    time_log: '',
    user: '',
    schedule_from: '',
    schedule_to: '',
    start_time: '',
    end_time: '',
    tech_id: '',
    tech_name: '',
    tech_notes: '',
    user_comment: '',
    multi_tech_ids: [],
  });

  // confirm delete
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    fetchOps();
    fetchTechnicians();
  }, []);

  const fetchOps = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${api}?list=1`);
      // Expect data = ['success', rows] or {success:true, data:[...]}
      let rows = [];
      if (Array.isArray(data) && data[0] === 'success') rows = data[1] || [];
      else if (data?.success && Array.isArray(data.data)) rows = data.data;
      else rows = Array.isArray(data) ? data : [];
      setOps(rows);
    } catch (err) {
      if (err.response?.status === 401) navigate('/login');
      else toast.error('Error loading operation schedules', { position: 'top-center' });
    } finally {
      setLoading(false);
    }
  };

  const fetchTechnicians = async () => {
    try {
      const { data } = await axios.get(`${apiUrl}/contracts_crud.php`, {
        params: { employee_list: 'true' },
        headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
      });
      setTechnicians(Array.isArray(data) ? data : []);
    } catch (err) {
      // non-blocking
    }
  };

  // local filters
const filtered = useMemo(() => {
  let rows = [...ops];

  if (search.trim()) {
    const s = search.toLowerCase();
    rows = rows.filter((r) => {
      const hay = [
        r.description,
        r.tech_name,
        r.user,
        r.status,
        r.tech_notes,
        r.user_comment,
        r.id,               // numeric id
        r.tech_id,          // numeric tech id
        r.schedule_from,
        r.schedule_to,
        r.date,
      ]
        .map(v => (v ?? '').toString().toLowerCase());

      return hay.some(h => h.includes(s));
    });
  }

  if (statusFilter !== 'all') {
    rows = rows.filter(
      r => (r.status || '').toLowerCase() === statusFilter.toLowerCase()
    );
  }
  return rows;
}, [ops, search, statusFilter]);

  const openNew = () => {
    setIsEditing(false);
    setFormData({
      id: '',
      description: '',
      status: 'Scheduled',
      date: new Date().toISOString().slice(0,10),
      time_log: new Date().toTimeString().slice(0,8),
      user: '', // backend will fill with logged user if you prefer
      schedule_from: '',
      schedule_to: '',
      start_time: '',
      end_time: '',
      tech_id: '',
      tech_name: '',
      tech_notes: '',
      user_comment: '',
      multi_tech_ids: [],
    });
    setIsModalOpen(true);
  };

  const openEdit = (row) => {
    setIsEditing(true);
    setFormData({
      id: row.id,
      description: row.description || '',
      status: row.status || '',
      date: row.date || '',
      time_log: row.time_log || '',
      user: row.user || '',
      schedule_from: row.schedule_from || '',
      schedule_to: row.schedule_to || '',
      start_time: row.start_time || '',
      end_time: row.end_time || '',
      tech_id: row.tech_id || '',
      tech_name: row.tech_name || '',
      tech_notes: row.tech_notes || '',
      user_comment: row.user_comment || '',
      multi_tech_ids: Array.isArray(row.multi_tech_ids)
        ? row.multi_tech_ids : row.multi_tech_ids
          ? JSON.parse(row.multi_tech_ids) : [],
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setIsEditing(false);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    // basic validation
    if (!formData.status || !formData.date || !formData.time_log) {
      toast.error('Status, Date and Time Log are required.', { position: 'top-center' });
      return;
    }
    if (formData.end_time && formData.start_time && formData.end_time <= formData.start_time) {
      toast.error('End time must be after start time.', { position: 'top-center' });
      return;
    }
    if (!formData.end_time && !formData.start_time) {
      toast.error('Time required', { position: 'top-center' });
      return;
    }
    if (formData.description.trim() === '') {
      toast.error('Description is required.', { position: 'top-center' });
      return;
    }
    try {
      const res = await axios.post(api, {
        status: 'recordOperation',
        data: formData
      }, { headers: { 'Content-Type': 'application/json' }});

      const ok = (res.data && res.data.success === true) ||
                 (Array.isArray(res.data) && res.data[0] === 'success');
      if (ok) {
        toast.success(isEditing ? 'Schedule updated!' : 'Schedule created!', { position: 'top-center' });
        closeModal();
        fetchOps();
      } else if (ok.overlaps?.length) {
        const names = ok.overlaps.map(item => item.tech_name).join(', ');
        toast.error(`Overlapping schedule found for: ${names}`);
        return;
      } else {
        toast.error(res.data?.error || 'Failed to save', { position: 'top-center' });
      }
    } catch (err) {
      if (err.response?.status === 401) navigate('/login');
      else toast.error('Server error saving schedule', { position: 'top-center' });
    }
  };

  const handleDeleteRequest = (row) => {
    setDeleteId(row.id);
    setShowConfirm(true);
  };

  const confirmDelete = async () => {
    setShowConfirm(false);
    try {
      const res = await axios.post(api, {
        status: 'deleteOperation',
        data: { id: deleteId }
      }, { headers: { 'Content-Type': 'application/json' }});

      const ok = (res.data && res.data.success === true) ||
                 (Array.isArray(res.data) && res.data[0] === 'success');

      if (ok) {
        toast.success('Deleted', { position: 'top-center' });
        fetchOps();
      } else {
        toast.error(res.data?.error || 'Delete failed', { position: 'top-center' });
      }
    } catch (err) {
      if (err.response?.status === 401) navigate('/login');
      else toast.error('Server error deleting', { position: 'top-center' });
    }
  };

  const cancelDelete = () => setShowConfirm(false);

  const getLinksForRow = (row) => ([
    { name: 'Edit', click: () => openEdit(row) },
    { name: 'Delete', click: () => handleDeleteRequest(row) },
  ]);

  const handleSearchClick = async () => {
    setLoading(true);
    const bodyData = {
      status: 'search',
      data: { search: search }
    };

    try {
      const response = await axios.post(api, bodyData, {
        headers: { 'Content-Type': 'application/json' }
      });
      if (response.data[0] === 'success' && Array.isArray(response.data[1])) {
        setOps(response.data[1]);
        setSearch(''); // avoid double-filtering out server results
      } else {
        setOps([]);
        toast.error('Not found: ' + search, { position: 'top-center' });
      }
    } catch (error) {
      //console.error('Error searching active jobs', error);
    } finally {
      setLoading(false);
    }
  };  

const formatTimeNY = (timeString) => {
  if (!timeString) return '-';

  const [hours, minutes] = timeString.split(':');

  const date = new Date();
  date.setHours(parseInt(hours, 10));
  date.setMinutes(parseInt(minutes, 10));

  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'America/New_York',
  });
};


  return (
    <div className="mx-auto max-w-[1250px] px-4 sm:px-6 lg:px-8 py-8">
      <HeadingCp label="Operation Schedule" />

      <>{/* //codex-- Match this page's toolbar to its asmainframe reference. */}<PageToolbar title="Operation schedule tools" actions={<>
          <button type="button" onClick={openNew} className="px-4 py-2 rounded text-white bg-green-600 hover:bg-green-700">New Schedule +</button>
          <NavLink to="/full_scheduler_viewer" className="ml-auto">
            <button className="px-4 py-2 rounded text-white bg-indigo-600 hover:bg-indigo-700">Full Scheduler</button>
          </NavLink>
        </>}>
          <ToolbarField label="Status"><select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="border rounded px-3 py-2">
              <option value="all">All Status</option>
              <option value="Scheduled">Scheduled</option>
              <option value="In Progress">In Progress</option>
              <option value="Completed">Completed</option>
              <option value="Cancelled">Cancelled</option>
            </select></ToolbarField>
          <SearchBarCP search={search} handleChange={e => setSearch(e.target.value)} onClick={handleSearchClick} />
        </PageToolbar></>

      {loading ? (
        <LoadingSpinner>Loading...</LoadingSpinner>
      ) : (
        <table className="min-w-full divide-y divide-gray-300 bg-gray-200 border rounded-lg">
          <thead>
            <tr className="divide-x divide-gray-400">
              <th className="px-3 py-3 text-left text-sm font-semibold">ID</th>
              <th className="px-3 py-3 text-left text-sm font-semibold">Description</th>
              <th className="px-3 py-3 text-left text-sm font-semibold">Date(s)</th>
              <th className="px-3 py-3 text-left text-sm font-semibold">Time</th>
              <th className="px-3 py-3 text-left text-sm font-semibold">Status</th>
              <th className="px-3 py-3 text-left text-sm font-semibold">Report</th>
              <th className="px-3 py-3 text-left text-sm font-semibold">Tech</th>
              <th className="px-3 py-3 text-left text-sm font-semibold">Tech|Admin Notes</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center py-6 text-gray-400">No schedules.</td>
              </tr>
            ) : (
              filtered.map((r, idx) => (
                <tr key={r.id} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-100'} divide-x`}>
                  <td className="px-3 py-2 text-sm">
                    <SubMenu label={r.id} idItem={r.id} links={getLinksForRow(r)} />
                    <div className='text-xs text-gray-400'>{r.user}</div>
                  </td>
                  <td className="px-3 py-2 text-sm">
                    <div className='text-sm text-gray-800'>{r.description}</div>
                  </td>
                  <td className="px-3 py-2 text-sm">
                    <div><DateFormat date={r.schedule_from || r.date} /></div>
                    {r.schedule_to && (
                      <div className="text-xs text-gray-500">to <DateFormat date={r.schedule_to} /></div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-sm">
                    {formatTimeNY(r.start_time)} — {formatTimeNY(r.end_time)}
                    <div className="text-xs text-gray-500">
                      log:{formatTimeNY(r.time_log)}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-sm">{r.status}</td>             
                  <td className="px-3 py-2 text-sm">
                    {r.status === 'Completed' && (
                      <NavLink
                        to={`/opreport/${r.id}`}
                        target="_blank"
                        className={"flex items-center"}
                        rel="noopener noreferrer"
                      >
                        <span><DocumentCheckIcon className="w-5 text-green-600 font-bold" /></span>
                        <span className='text-green-600 '>Report</span>
                      </NavLink>
                    )}
                  </td>                  
                  <td className="px-3 py-2 text-sm">
                    <div className="font-medium">{r.tech_name || '-'}</div>
                    <div className="text-xs text-gray-500">#{r.tech_id ?? '-'}</div>
                  </td>     
                  <td className="px-3 py-2 text-sm max-w-xs">
                    {r.tech_notes && (
                      <span
                        className="flex items-center mb-1 hover:cursor-pointer text-indigo-700"
                        onClick={() => setNotesIdOpen(r.id)}
                      >
                        <DocumentCheckIcon className="w-5" /> Tech Notes
                      </span>
                    )}
                    
                    {notesIdOpen === r.id && (
                      <ModalWindow
                        job={r.tech_notes}
                        closeNotes={() => setNotesIdOpen(null)}
                        activeButton={true}
                      />
                    )}

                    {/* admin notes */}
                    {r.user_comment && (
                      <span
                        className="flex items-center mb-1 hover:cursor-pointer text-orange-700 mt-2"
                        onClick={() => setUserNotesIdOpen(r.id)}
                      >
                        <DocumentCheckIcon className="w-5" /> Admin Notes
                      </span>
                    )}

                    {userNotesIdOpen === r.id && (
                      <ModalWindow
                        job={r.user_comment}
                        closeNotes={() => setUserNotesIdOpen(null)}
                        activeButton={true}
                      />
                    )}                    
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}

      {/* Form Modal */}
      <OperationFormModal
        //codex-- Offer private reusable defaults when creating an operation schedule from this page.
        enableTemplates
        templateApiUrl={apiUrl}
        isOpen={isModalOpen}
        onClose={()=>setIsModalOpen(false)}
        isEditing={isEditing}
        formData={formData}
        setFormData={setFormData}
        technicians={technicians}
        onSubmit={handleFormSubmit}
      />

      {/* Confirm Delete */}
      {showConfirm && (
        <div className="fixed z-50 inset-0 bg-black/40 flex items-center justify-center">
          <div className="bg-white rounded shadow p-5 min-w-[320px]">
            <h3 className="text-lg font-semibold mb-2">Confirm Deletion</h3>
            <p className="text-sm text-gray-600 mb-4">Are you sure you want to delete schedule #{deleteId}?</p>
            <div className="flex justify-end gap-2">
              <button className="px-4 py-2 rounded border" onClick={cancelDelete}>Cancel</button>
              <button className="px-4 py-2 rounded bg-red-600 text-white" onClick={confirmDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OperationScheduleListView;
